// Per-basket serialisation of cart-mutating API calls (staging 2026-09-25, the measured tax
// doubling). The checkout step fires applyDeliveryCharge twice ~1.7s apart (the address effect
// and the delivery-option effect both run on entry), and the backend runs both concurrently:
// each pass is a whole-cart tax DELETE followed by one POST per line (task-556), so when the
// second pass's DELETE lands before the first pass's remaining POSTs - or 404s against an
// item EP hasn't made readable yet (read-after-write lag, observed live: a DELETE 404'd 28ms
// after a successful POST) - every line ends up with TWO tax items. Sequential passes are
// proven safe (the second strip cleans the first's items and re-adds once), so the fix is to
// never let two mutations on the SAME basket overlap: a per-basket FIFO promise chain.
//
// This also fixes response ordering: overlapping calls resolved out of order (the older,
// slower response landed last and overwrote the newer basket state in setBasket - the
// "shipping unselected on entering checkout" symptom). A FIFO chain resolves in call order,
// so the freshest server state is always the last write into the basket context.
//
// Deliberately NOT latest-wins: every queued call still runs and resolves for its own caller
// (each call site owns UI state in its .then), and a superseded pass is harmless once
// sequential - the next pass strips and re-quotes over it.
//
// Authored as CommonJS (matching appliedPromoCodes.js) so it unit-tests directly under
// node --test while staying import-able from the ESM service layer:
//
//     node --test src/services/basketMutationQueue.test.js

/** Per-key chain tails. An entry exists only while a call for that key is running or queued. */
const chainTails = new Map()

// PR #2212 review round 2 (1): a request that never returns must not wedge the whole basket -
// with the chain, one stalled fetch used to block every later mutation until the browser's own
// network timeout (minutes, or never). The timeout is ABORT-based deliberately: each task
// receives an AbortSignal that the underlying fetch honours. Merely releasing the wait without
// the abort would reopen the concurrent-mutation overlap this queue exists to close. 60s is
// generous against the measured latencies (ApplyDeliveryCharge ~3.7s worst observed; checkout
// p99 ~10s) - it exists to convert "minutes to forever" into a bounded failure the caller's
// existing catch handles (error notification, disabled cleared).
//
// Round 3 (3) - what the abort actually does, honestly: it cancels the request CLIENT-side.
// The backend does not observe the disconnect mid-pass, so its strip/re-add may still be
// running server-side at the moment of abort. Starting the next task right then could overlap
// it - the exact disease. So after a TIMEOUT abort (never after a normal settle) the chain
// holds a drain grace before releasing the next task: the server pass gets timeout + grace in
// total (90s for mutations) before anything else touches the cart. A pass still mutating
// beyond that is accepted as residual risk - it is far beyond every measured latency, and the
// placement gate (Count() > 1) plus placement's own strip still protect billing.
const MutationTimeoutMs = 60000
const AbortDrainGraceMs = 30000

// Round 3 (#187 (2)) + round 4 (#187): checkoutBasket/preparePayment join the chain with THIS
// timeout instead of none - a full exemption meant one stalled Place Order wedged every later
// mutation on the basket until a page reload. Round 4 raised it from 120s to 300s: an aborted
// checkout looks like an ordinary failure to the page, which re-enables Place Order, and a
// retry while the server is still placing the first order is a DOUBLE ORDER. 300s matches the
// browser's own network give-up, so this timeout introduces NO ambiguity window that does not
// already exist today (pre-queue, a checkout hung this long failed the same way with the same
// retry-duplicate risk) - it only bounds the queue wedge at 5 minutes instead of forever. Do
// NOT lower it without solving retry idempotency first (the backend has no idempotency key;
// each click sends a fresh requestId).
const CheckoutTimeoutMs = 300000

/**
 * Run `task` exclusively for `key`: tasks sharing a key run strictly one at a time, in call
 * order; tasks with different keys are independent. The returned promise settles exactly as
 * the task's does (a rejection reaches ONLY that task's caller and never breaks the chain).
 *
 * @template T
 * @param {string} key - the basket id the mutation targets
 * @param {(signal: AbortSignal) => Promise<T>} task - deferred so the call does not START
 *   until its turn; the signal aborts the task's request when the timeout fires
 * @param {number} [timeoutMs] - override for checkout/tests
 * @param {number} [drainGraceMs] - override for tests
 * @returns {Promise<T>}
 */
function runExclusive(key, task, timeoutMs = MutationTimeoutMs, drainGraceMs = AbortDrainGraceMs) {
  const tail = chainTails.get(key) ?? Promise.resolve()
  let timedOut = false
  const result = tail.then(() => {
    const abortController = new AbortController()
    const timer = setTimeout(() => {
      timedOut = true
      // Round 4 (#187): a timeout abort must leave a trace - the request's outcome is UNKNOWN
      // (the server may still be processing it) and the caller's generic failure path will not
      // say why. Console always; App Insights via the bundled logger when available (the lazy
      // require keeps this module loadable under plain node --test, where the logger's
      // AppInsights import chain cannot resolve).
      const message = `[basketMutationQueue] basket ${key}: queued request aborted after ${timeoutMs}ms - outcome unknown, the server may still be processing it`
      console.error(message)
      try {
        // eslint-disable-next-line global-require
        const { logWarning } = require('./loggingService')
        logWarning(message, null)
      } catch (loggerUnavailable) {
        // node --test / SSR: the console line above is the trace.
      }
      abortController.abort()
    }, timeoutMs)
    return Promise.resolve(task(abortController.signal)).finally(() => clearTimeout(timer))
  })

  // The stored tail swallows the outcome so one failure cannot reject every queued successor;
  // callers still see their own task's rejection through `result`. After a TIMEOUT abort the
  // tail holds the drain grace (round 3 (3)): the abort is client-side only, so the server may
  // still be finishing the aborted pass - the next task must not start on top of it. A normal
  // settle (including an ordinary rejection) releases immediately.
  const settled = result
    .then(
      () => undefined,
      () => undefined
    )
    .then(() =>
      timedOut ? new Promise((resolve) => setTimeout(resolve, drainGraceMs)) : undefined
    )
  chainTails.set(key, settled)
  settled.then(() => {
    // Drop the entry once the chain drains, so finished baskets do not accumulate. Only the
    // CURRENT tail may clean up - a newer queued call has already replaced `settled`.
    if (chainTails.get(key) === settled) {
      chainTails.delete(key)
    }
  })

  return result
}

module.exports = {
  runExclusive,
  CheckoutTimeoutMs,
}
